import i18next from 'i18next';

import type { ImageFormat } from '@egr/xbox/utils/FileFormats';
import { CropMargins } from '@egr/xbox/utils/Canvas';
import { imageUrlToCanvas, ImageSize } from '@egr/xbox/utils/Image';

import { SceneElement, ElementCategory } from '@egr/wcf/modules/core/mdl';
import type { MainArticleElement } from '@egr/wcf/modules/cf';

export interface SnapshotOptions {
    withDimensions?: boolean,
    transparent: boolean,
    /** mui theme paper background color */
    lightBackgroundColor?: string;
    /** mui theme paper background color in darkmode */
    darkBackgroundColor?: string;
    /** handling must be implemented in project (not available by default) */
    margins?: CropMargins;
}

export interface SnapshotResult {
    result: Blob | null,
    size: ImageSize,
    imageFormat: ImageFormat
}

export function addDimensionInfo(
    pixelRatio: number = 1,
    fontSize: number = 12,
    margins: CropMargins | null = null,
): (ctx: CanvasRenderingContext2D) => Promise<void> {
    return async (ctx: CanvasRenderingContext2D): Promise<void> => {
        ctx.font = `${fontSize * pixelRatio}px Open Sans, sans-serif`;
        ctx.fillStyle = '#868686';
        ctx.fillText(
            i18next.t('Dimensions may vary'),
            14.5 * pixelRatio + (margins?.left ?? 0),
            ctx.canvas.height - (14.5 * pixelRatio) - (margins?.bottom ?? 0)
        );
    };
}

export function isSelectableElement(element: SceneElement): boolean {
    return element.canSelect();
}

export function isDeletableElement(element: SceneElement): boolean {
    return (
        element.canDelete()
    );
}

export function elementCategories(previous: Array<ElementCategory>, current: SceneElement): Array<ElementCategory> {
    if (previous.indexOf(current.category) === -1) {
        return [...previous, current.category];
    }
    return previous;
}

export async function getCanvasWithDimensionsInfo(
    sourceUrl: string,
    pixelRatio: number,
    fontSize: number,
): Promise<HTMLCanvasElement | undefined> {
    const canvas: HTMLCanvasElement | undefined = await imageUrlToCanvas(sourceUrl);
    if (canvas == null) {
        return;
    }

    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (ctx == null) {
        return;
    }

    await addDimensionInfo(pixelRatio, fontSize)(ctx);

    return canvas;
}

export function marginsToValidIntegers(margins?: CropMargins): CropMargins | undefined {
    if (!margins) {
        return undefined;
    }

    return {
        top: Math.round(margins.top ?? 0),
        right: Math.round(margins.right ?? 0),
        bottom: Math.round(margins.bottom ?? 0),
        left: Math.round(margins.left ?? 0)
    };
}

export function selectableElementsCount(previous: number, current: MainArticleElement): number {
    return previous + current.getSubArticles(false).filter(isSelectableElement).length + (isSelectableElement(current) ? 1 : 0);
}